import { useEffect, useMemo, useRef, useState } from "react";

export type EmailInboxRefreshStatusTone =
  | "neutral"
  | "busy"
  | "success"
  | "error";

interface UseEmailInboxRefreshFeedbackInput {
  isTriggeringSync: boolean;
  isSyncing: boolean;
  isProcessingEmails: boolean;
  hasError: boolean;
  isListRefetching: boolean;
}

const SUCCESS_FLASH_MS = 3000;
const SUCCESS_DEBOUNCE_MS = 150;

export function useEmailInboxRefreshFeedback({
  isTriggeringSync,
  isSyncing,
  isProcessingEmails,
  hasError,
  isListRefetching,
}: UseEmailInboxRefreshFeedbackInput) {
  const [showSuccess, setShowSuccess] = useState(false);
  const prevActiveRef = useRef(false);
  const successDebounceRef = useRef<number | null>(null);
  const successClearRef = useRef<number | null>(null);

  const isRefreshActive =
    isTriggeringSync ||
    isSyncing ||
    isProcessingEmails ||
    isListRefetching;

  useEffect(() => {
    const wasActive = prevActiveRef.current;
    prevActiveRef.current = isRefreshActive;

    if (successDebounceRef.current !== null) {
      window.clearTimeout(successDebounceRef.current);
      successDebounceRef.current = null;
    }

    if (isRefreshActive) {
      setShowSuccess(false);
      if (successClearRef.current !== null) {
        window.clearTimeout(successClearRef.current);
        successClearRef.current = null;
      }
      return;
    }

    if (!wasActive) return;

    successDebounceRef.current = window.setTimeout(() => {
      successDebounceRef.current = null;
      if (prevActiveRef.current || hasError) return;
      setShowSuccess(true);
      successClearRef.current = window.setTimeout(() => {
        successClearRef.current = null;
        setShowSuccess(false);
      }, SUCCESS_FLASH_MS);
    }, SUCCESS_DEBOUNCE_MS);
  }, [isRefreshActive, hasError]);

  useEffect(() => {
    return () => {
      if (successDebounceRef.current !== null) {
        window.clearTimeout(successDebounceRef.current);
      }
      if (successClearRef.current !== null) {
        window.clearTimeout(successClearRef.current);
      }
    };
  }, []);

  const statusLabel = useMemo(() => {
    if (hasError) return "Sync failed";
    if (showSuccess) return "Up to date";
    if (isTriggeringSync) return "Starting sync…";
    if (isSyncing) return "Syncing mailbox…";
    if (isProcessingEmails) return "Updating emails…";
    if (isListRefetching) return "Refreshing list…";
    return "";
  }, [
    hasError,
    showSuccess,
    isTriggeringSync,
    isSyncing,
    isProcessingEmails,
    isListRefetching,
  ]);

  const statusTone = useMemo<EmailInboxRefreshStatusTone>(() => {
    if (hasError) return "error";
    if (showSuccess) return "success";
    if (isRefreshActive) return "busy";
    return "neutral";
  }, [hasError, showSuccess, isRefreshActive]);

  return {
    isRefreshActive,
    statusLabel,
    showSuccess,
    statusTone,
  };
}
